import {
  type FieldError,
  isErrorCode,
  PRODUCT_NAME,
  problemDetailsSchema,
} from '@wednesday/contracts';

/**
 * A failed API call (ADR-0010). `code` comes from the error catalog; `requestId` is shown to people as
 * "Reference" so any problem can be traced to its logs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string | undefined;
  readonly requestId: string | undefined;
  readonly fieldErrors: readonly FieldError[];
  readonly details: Readonly<Record<string, unknown>>;

  constructor(init: {
    status: number;
    code: string;
    title: string;
    detail?: string | undefined;
    requestId?: string | undefined;
    fieldErrors?: readonly FieldError[] | undefined;
    details?: Readonly<Record<string, unknown>> | undefined;
  }) {
    super(`${init.code}: ${init.title}`);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.detail = init.detail;
    this.requestId = init.requestId;
    this.fieldErrors = init.fieldErrors ?? [];
    this.details = init.details ?? {};
  }

  is(code: string): boolean {
    return this.code === code;
  }
}

/** Builds an ApiError from a failed response body (Problem Details, or anything else). */
export function apiErrorFromResponse(
  status: number,
  body: unknown,
  requestIdHeader: string | null,
): ApiError {
  const problem = problemDetailsSchema.safeParse(body);
  if (problem.success) {
    return new ApiError({
      status,
      code: problem.data.code,
      title: problem.data.title,
      detail: problem.data.detail,
      requestId: problem.data.requestId,
      fieldErrors: problem.data.errors,
      details: problem.data.details,
    });
  }
  return new ApiError({
    status,
    code: status >= 500 ? 'INTERNAL' : 'UNEXPECTED_RESPONSE',
    title:
      status >= 500 ? 'Something went wrong on our side.' : 'Unexpected response from the server.',
    requestId: requestIdHeader ?? undefined,
  });
}

/** For errors that never reached the server (offline, DNS, CORS). */
export function networkError(cause: unknown): ApiError {
  const error = new ApiError({
    status: 0,
    code: 'NETWORK_ERROR',
    title: `Cannot reach ${PRODUCT_NAME}. Check your connection and try again.`,
  });
  error.cause = cause;
  return error;
}

/** Human message for any thrown value: title (+ detail). */
export function describeError(error: unknown): {
  title: string;
  detail?: string;
  reference?: string;
} {
  if (error instanceof ApiError) {
    return {
      title: error.title,
      ...(error.detail ? { detail: error.detail } : {}),
      ...(error.requestId ? { reference: error.requestId } : {}),
    };
  }
  return { title: 'Something went wrong.' };
}

export { isErrorCode };
